import '../App.css';
import '../style.css';
import '../font.css';
import '../workDetailPage/style_works.css';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  copyArticleSectionUrlToClipboard,
  slugifyFragmentId,
} from '../utils/articleSectionLink';
import { ReactComponent as IconSlashSemicircle } from '../image/icon_slash_semicircle.svg';
import { useLanguage } from '../contexts/LanguageContext';

const HOVER_TOOLTIP_MS = 500;
const COPIED_TOAST_MS = 2000;

export function ArticleTextSection(props: {
    title?: ReactNode;
    /** 명시 id. 없으면 `title`이 문자열일 때만 slug로 자동 생성됩니다. 중복 제목이면 반드시 지정하세요. */
    sectionId?: string;
    subTitle?: ReactNode;
    description? : React.ReactNode;
    subDescription? : React.ReactNode;
    marginTop?: boolean;
    marginBottom?: boolean;
    /**
     * true이면 언어 토글에 따라 title/description 자리에 subTitle/subDescription(영문)을 보여주고,
     * subTitle/subDescription은 별도 영역으로 노출하지 않는다. (WorkTextSection과 달리 한 칸만 보여줌)
     */
    swapByLanguage?: boolean;
    }) {
    const marginTopStyle = props.marginTop ? '40px' : '0px';
    const marginBottomStyle = props.marginBottom ? '20px' : '0px';

    const { language } = useLanguage();
    const useEnglishSwap = props.swapByLanguage && language === 'en';
    const displayTitle = useEnglishSwap && props.subTitle ? props.subTitle : props.title;
    const displayDescription =
      useEnglishSwap && props.subDescription ? props.subDescription : props.description;
    const showSubTitle = !props.swapByLanguage && props.subTitle;
    const showSubDescription = !props.swapByLanguage && props.subDescription;

    const domSectionId =
      props.sectionId?.trim() ||
      (typeof props.title === 'string' && props.title.trim()
        ? slugifyFragmentId(props.title)
        : undefined);

    const [longHoverTooltip, setLongHoverTooltip] = useState(false);
    const [copiedToast, setCopiedToast] = useState(false);
    const hoverDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const copiedResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearHoverTooltipTimer = useCallback(() => {
      if (hoverDelayRef.current) {
        clearTimeout(hoverDelayRef.current);
        hoverDelayRef.current = null;
      }
    }, []);

    const handleWrapMouseEnter = useCallback(() => {
      clearHoverTooltipTimer();
      hoverDelayRef.current = setTimeout(() => {
        setLongHoverTooltip(true);
      }, HOVER_TOOLTIP_MS);
    }, [clearHoverTooltipTimer]);

    const handleWrapMouseLeave = useCallback(() => {
      clearHoverTooltipTimer();
      setLongHoverTooltip(false);
    }, [clearHoverTooltipTimer]);

    const handleCopySectionLink = useCallback(async () => {
      if (!domSectionId) return;
      clearHoverTooltipTimer();
      setLongHoverTooltip(false);
      const ok = await copyArticleSectionUrlToClipboard(domSectionId);
      if (ok) {
        if (copiedResetRef.current) clearTimeout(copiedResetRef.current);
        setCopiedToast(true);
        copiedResetRef.current = setTimeout(() => setCopiedToast(false), COPIED_TOAST_MS);
      }
    }, [domSectionId, clearHoverTooltipTimer]);

    const handleTitleRowClick = useCallback(
      (_e: MouseEvent<HTMLDivElement>) => {
        if (!domSectionId) return;
        const sel = window.getSelection();
        if (sel && sel.toString().trim().length > 0) return;
        void handleCopySectionLink();
      },
      [domSectionId, handleCopySectionLink]
    );

    const handleTitleRowKeyDown = useCallback(
      (e: KeyboardEvent<HTMLDivElement>) => {
        if (!domSectionId) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          void handleCopySectionLink();
        }
      },
      [domSectionId, handleCopySectionLink]
    );

    useEffect(
      () => () => {
        clearHoverTooltipTimer();
        if (copiedResetRef.current) clearTimeout(copiedResetRef.current);
      },
      [clearHoverTooltipTimer]
    );

    const showTooltip = longHoverTooltip || copiedToast;
    const tooltipLabel = copiedToast ? '복사완료!' : '링크 복사';

    return (
        <>
            <div 
                className={'articletextsection fadeIn'}
                id={domSectionId}
                style={{
                    marginTop: marginTopStyle,
                    marginBottom: marginBottomStyle,
                    scrollMarginTop: '1rem',
                }}
            >
                {displayTitle && (
                  <div
                    className={
                      'articleTitleRow' +
                      (domSectionId ? ' articleTitleRow--interactive' : '')
                    }
                    onClick={domSectionId ? handleTitleRowClick : undefined}
                    onMouseEnter={domSectionId ? handleWrapMouseEnter : undefined}
                    onMouseLeave={domSectionId ? handleWrapMouseLeave : undefined}
                    onKeyDown={domSectionId ? handleTitleRowKeyDown : undefined}
                    role={domSectionId ? 'button' : undefined}
                    tabIndex={domSectionId ? 0 : undefined}
                    aria-label={
                      domSectionId ? '이 섹션 링크 클립보드에 복사' : undefined
                    }
                  >
                    <div className="articleTitle">{displayTitle}</div>
                    {domSectionId && (
                      <div className="articleSectionCopyLinkWrap">
                        <span
                          className="articleSectionCopyLinkBtn"
                          aria-hidden
                        >
                          <span className="articleSectionCopyLinkIcon">
                            <IconSlashSemicircle width={24} height={24} />
                          </span>
                        </span>
                        {showTooltip && (
                          <>
                            <span
                              className="articleSectionCopyLinkHoverPad"
                              aria-hidden
                            />
                            <span
                              className="articleSectionCopyLinkTooltip"
                              role="tooltip"
                            >
                              {tooltipLabel}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className='articleDescriptionContainer'>
                    {showSubTitle && <div className='articleSubTitle'>{props.subTitle}</div>}
                    <div className='textsection-sub'>
                        {displayDescription && <div className='articleDescription'>{displayDescription}</div>}
                        {showSubDescription && <div className='articleSubdescription' >{props.subDescription}</div>}
                    </div>
                </div>
                
            </div>
        </>
      )
}
